import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import {
  History, Loader2, ArrowLeft, MapPin, Tag, TrendingUp, TrendingDown,
  Minus, GitCompareArrows, Check, Camera
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const metricValue = (m) => {
  if (!m) return null;
  if (m.count !== null && m.count !== undefined) return { label: 'count', value: m.count, unit: '' };
  if (m.area !== null && m.area !== undefined) return { label: 'area', value: m.area, unit: `${m.unit || 'm'}²` };
  return null;
};

const Delta = ({ a, b }) => {
  const va = metricValue(a);
  const vb = metricValue(b);
  if (!va || !vb || va.label !== vb.label) {
    return <span className="text-slate-400 text-sm">—</span>;
  }
  const diff = Number(vb.value) - Number(va.value);
  const pct = Number(va.value) !== 0 ? (diff / Math.abs(Number(va.value))) * 100 : null;
  const up = diff > 0, down = diff < 0;
  const color = up ? 'text-emerald-600' : down ? 'text-red-600' : 'text-slate-500';
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${color}`} data-testid="compare-delta">
      <Icon className="w-4 h-4" />
      {diff > 0 ? '+' : ''}{Number(diff.toFixed(2))} {va.unit}
      {pct !== null && <span className="text-xs">({diff > 0 ? '+' : ''}{pct.toFixed(1)}%)</span>}
    </span>
  );
};

const ScanCard = ({ job, selected, onToggle }) => {
  const mv = metricValue(job.metrics);
  return (
    <button
      onClick={onToggle}
      data-testid="history-scan-card"
      className={`flex-shrink-0 w-52 text-left rounded-xl border-2 transition-all ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-300'} bg-white overflow-hidden`}
    >
      <div className="h-28 bg-slate-100 flex items-center justify-center overflow-hidden relative">
        {job.imageUrl ? (
          <img src={`${BACKEND_URL}${job.imageUrl}`} alt="scan" className="w-full h-full object-cover" />
        ) : (
          <Camera className="w-7 h-7 text-slate-300" />
        )}
        {selected && (
          <span className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-1"><Check className="w-3 h-3" /></span>
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-slate-500">{fmtDate(job.createdAt)}</p>
        <p className="text-sm font-semibold text-slate-800 truncate">{job.roomName || job.type}</p>
        {mv ? (
          <p className="text-lg font-bold text-slate-900">{Number(mv.value)}<span className="text-xs font-normal text-slate-500 ml-1">{mv.unit || mv.label}</span></p>
        ) : (
          <p className="text-sm text-slate-400">—</p>
        )}
      </div>
    </button>
  );
};

const LocationGroup = ({ group }) => {
  const { t } = useTranslation();
  const [sel, setSel] = useState([]);

  const toggle = (id) => {
    setSel((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const jobA = group.jobs.find((j) => j.id === sel[0]);
  const jobB = group.jobs.find((j) => j.id === sel[1]);
  const comparable = group.jobs.length >= 2;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="history-location-group">
      <div className="flex items-center gap-2 mb-4">
        {group.key.startsWith('pin:') ? <MapPin className="w-5 h-5 text-blue-500" /> : <Tag className="w-5 h-5 text-purple-500" />}
        <h3 className="text-lg font-semibold text-slate-900">{group.label}</h3>
        <span className="text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{group.jobs.length} scans</span>
      </div>

      {comparable && (
        <p className="text-sm text-slate-500 mb-3 flex items-center gap-1"><GitCompareArrows className="w-4 h-4" /> {t('history.selectTwo')}</p>
      )}

      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="history-timeline">
        {group.jobs.map((j) => (
          <ScanCard key={j.id} job={j} selected={sel.includes(j.id)} onToggle={() => toggle(j.id)} />
        ))}
      </div>

      {jobA && jobB && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center bg-slate-50 rounded-lg border border-slate-200 p-4" data-testid="history-comparison">
          <CompareSide job={jobA} />
          <div className="text-center">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">{t('history.delta')}</p>
            <Delta a={jobA.metrics} b={jobB.metrics} />
            <p className="text-xs text-slate-400 mt-1">{fmtDate(jobA.createdAt)} → {fmtDate(jobB.createdAt)}</p>
          </div>
          <CompareSide job={jobB} />
        </div>
      )}
    </div>
  );
};

const CompareSide = ({ job }) => {
  const mv = metricValue(job.metrics);
  return (
    <div className="text-center">
      {job.imageUrl && <img src={`${BACKEND_URL}${job.imageUrl}`} alt="scan" className="w-full h-24 object-cover rounded-lg mb-2 border border-slate-200" />}
      <p className="text-xs text-slate-500">{fmtDate(job.createdAt)}</p>
      <p className="text-2xl font-bold text-slate-900">{mv ? Number(mv.value) : '—'}<span className="text-xs font-normal text-slate-500 ml-1">{mv ? (mv.unit || mv.label) : ''}</span></p>
    </div>
  );
};

const UnlinkedRow = ({ job, onTagged }) => {
  const [tag, setTag] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!tag.trim()) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/analysis-jobs/${job.id}/location-tag`, { locationTag: tag.trim() });
      toast.success('Location tagged');
      onTagged();
    } catch (e) {
      toast.error('Failed to tag location');
    } finally {
      setSaving(false);
    }
  };
  const mv = metricValue(job.metrics);
  return (
    <div className="flex items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid="history-unlinked-row">
      <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
        {job.imageUrl ? <img src={`${BACKEND_URL}${job.imageUrl}`} alt="" className="w-full h-full object-cover" /> : <Camera className="w-5 h-5 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate">{job.roomName || job.type}</p>
        <p className="text-xs text-slate-500">{fmtDate(job.createdAt)} · {mv ? `${Number(mv.value)} ${mv.unit || mv.label}` : '—'}</p>
      </div>
      <input
        value={tag}
        onChange={(e) => setTag(e.target.value)}
        placeholder="Add location tag"
        className="w-40 px-3 py-1.5 text-sm border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="history-tag-input"
      />
      <button onClick={save} disabled={saving || !tag.trim()} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="history-tag-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
      </button>
    </div>
  );
};

const VersionHistory = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/history`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [projectId]);

  const groups = data?.groups || [];
  const unlinked = data?.unlinked || [];
  const isEmpty = groups.length === 0 && unlinked.length === 0;

  return (
    <Layout>
      <div className="space-y-6">
        <button onClick={() => navigate(`/app/projects`)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="history-back">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><History className="w-7 h-7 text-blue-600" /> {t('history.title')}</h1>
          <p className="text-slate-600 mt-1">{t('history.subtitle')}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('common.loading')}</div>
        ) : isEmpty ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400" data-testid="history-empty">{t('history.noData')}</div>
        ) : (
          <>
            {groups.map((g) => <LocationGroup key={g.key} group={g} />)}

            {unlinked.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="history-unlinked">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{t('history.unlinked')}</h3>
                <p className="text-sm text-slate-500 mb-3">{t('history.selectTwo')}</p>
                <div className="space-y-2">
                  {unlinked.map((j) => <UnlinkedRow key={j.id} job={j} onTagged={load} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default VersionHistory;
